// src/components/presets/BannerSidebar.tsx
'use client'

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Check } from 'lucide-react'
import type { BannerProps } from '../Banner'
import { getSidebarPositionStyles } from '../../utils/positioning'
import { getCategoryLabel, getCategoryLongDescription } from '../../utils/translation-helpers'

function CategoryItem({
  checked,
  disabled,
  onChange,
  label,
  description,
  accentColor,
}: {
  checked: boolean
  disabled?: boolean
  onChange?: () => void
  label: string
  description: string
  accentColor: string
}) {
  const id = React.useId()

  return (
    <div
      style={{
        padding: '14px',
        backgroundColor: checked ? `${accentColor}08` : '#ffffff',
        borderRadius: '10px',
        border: checked ? `2px solid ${accentColor}30` : '2px solid #e5e7eb',
        opacity: disabled ? 0.7 : 1,
        transition: 'all 0.2s',
      }}
    >
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        <div style={{ position: 'relative', marginTop: '2px' }}>
          <input
            id={id}
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            style={{ position: 'absolute', opacity: 0 }}
          />
          <label htmlFor={id} style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
            <div
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '4px',
                border: checked ? `2px solid ${accentColor}` : '2px solid #d1d5db',
                backgroundColor: checked ? accentColor : '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
              }}
            >
              {checked && (
                <Check
                  style={{ width: '13px', height: '13px', color: '#ffffff', strokeWidth: 3 }}
                />
              )}
            </div>
          </label>
        </div>
        <label htmlFor={id} style={{ flex: 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
            {label}
            {disabled && (
              <span
                style={{ fontSize: '11px', color: '#6b7280', fontWeight: 400, marginLeft: '6px' }}
              >
                (always active)
              </span>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.5 }}>{description}</div>
        </label>
      </div>
    </div>
  )
}

export function BannerSidebar(props: BannerProps) {
  const {
    config,
    texts,
    categories,
    consent,
    onAccept,
    onDecline,
    onClose,
    onConsentChange,
    accentColor,
    style,
  } = props
  const { closeButton, showRejectLink, descriptionMode = 'short', language = 'en' } = config
  const [showSettings, setShowSettings] = useState(false)

  const description =
    descriptionMode === 'long'
      ? texts.longDescription || texts.description
      : texts.shortDescription || texts.description

  const isLeft = style.position.includes('left')
  const initialX = isLeft ? -400 : 400
  const containerStyles = getSidebarPositionStyles(isLeft)
  const buttonBorderRadiusValue = style.borderRadius === 'sharp' ? '0' : '10px'

  const handleSaveSettings = () => {
    setShowSettings(false)
    onAccept()
  }

  return (
    <motion.div
      initial={style.animation.enabled ? { x: initialX, opacity: 0 } : { opacity: 1 }}
      animate={style.animation.enabled ? { x: 0, opacity: 1 } : { opacity: 1 }}
      exit={style.animation.enabled ? { x: initialX, opacity: 0 } : { opacity: 0 }}
      transition={{ duration: style.animation.duration, ease: 'easeOut' }}
      style={{ ...containerStyles, width: '100%', maxWidth: '400px' }}
    >
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: style.background,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
      >
        {/* Header */}
        <div style={{ borderBottom: '1px solid #f3f4f6', padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', margin: 0 }}>
              {texts.heading}
            </h3>
            {closeButton && (
              <button
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  onClose()
                }}
                style={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                aria-label={texts.closeButton}
              >
                <X style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: 1.6, marginBottom: '20px' }}>
            {description}
          </p>

          <AnimatePresence>
            {showSettings ? (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <div
                  style={{
                    marginBottom: '8px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#374151',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Cookie Categories
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {categories.map(category => (
                    <CategoryItem
                      key={category.id}
                      checked={consent[category.id] !== false}
                      disabled={category.required}
                      onChange={() =>
                        !category.required && onConsentChange?.(category.id, !consent[category.id])
                      }
                      label={getCategoryLabel(category, language)}
                      description={getCategoryLongDescription(category, language)}
                      accentColor={accentColor}
                    />
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div
                  style={{
                    padding: '16px',
                    borderRadius: '12px',
                    border: `2px solid ${accentColor}20`,
                    backgroundColor: `${accentColor}05`,
                  }}
                >
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        marginTop: '2px',
                        backgroundColor: accentColor,
                      }}
                    >
                      <Check style={{ width: '12px', height: '12px', color: '#ffffff' }} />
                    </div>
                    <div>
                      <p
                        style={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#111827',
                          marginBottom: '4px',
                        }}
                      >
                        Zabezpečené cookies
                      </p>
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
                        Používáme pouze nezbytné a analytické cookies pro zajištění nejlepšího
                        zážitku.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: '1px solid #f3f4f6',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <button
            onClick={showSettings ? handleSaveSettings : onAccept}
            style={{
              width: '100%',
              padding: '13px 16px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#ffffff',
              backgroundColor: accentColor,
              border: 'none',
              borderRadius: buttonBorderRadiusValue,
              cursor: 'pointer',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = '0.9')}
            onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
          >
            {showSettings ? texts.saveButton : texts.acceptButton}
          </button>

          <button
            onClick={() => setShowSettings(!showSettings)}
            style={{
              width: '100%',
              padding: '13px 16px',
              fontSize: '14px',
              fontWeight: 600,
              color: showSettings ? accentColor : '#374151',
              backgroundColor: 'transparent',
              border: showSettings ? `2px solid ${accentColor}` : '2px solid #e5e7eb',
              borderRadius: buttonBorderRadiusValue,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            {showSettings ? texts.cancelButton : texts.settingsButton}
          </button>

          {showRejectLink && !showSettings && (
            <button
              onClick={onDecline}
              style={{
                width: '100%',
                textAlign: 'center',
                fontSize: '13px',
                fontWeight: 500,
                color: '#6b7280',
                backgroundColor: 'transparent',
                border: 'none',
                padding: '8px',
                cursor: 'pointer',
                transition: 'color 0.2s',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = '#374151')}
              onMouseLeave={e => (e.currentTarget.style.color = '#6b7280')}
            >
              {texts.rejectLink || texts.declineButton}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  )
}
